import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Article } from '@dj/types';
import { apiFetch } from '@/lib/api';

const REVALIDATE_URL = import.meta.env.VITE_PUBLIC_WEB_URL
  ? `${import.meta.env.VITE_PUBLIC_WEB_URL}/api/revalidate`
  : null;
const REVALIDATE_SECRET = import.meta.env.VITE_REVALIDATE_SECRET ?? '';

function toSlug(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-');
}

type FormState = {
  title: string; slug: string; summary: string; content: string;
  category: string; status: 'draft' | 'published'; featured_image_url: string;
};

export default function ArticleEditorPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form, setForm] = useState<FormState>({
    title: '', slug: '', summary: '', content: '',
    category: 'Engineering', status: 'draft', featured_image_url: '',
  });
  const [slugManual, setSlugManual] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!isEdit) return;
    apiFetch<Article>(`/api/v1/admin/articles/${id}`).then((res) => {
      if (res.success) {
        setForm({
          title: res.data.title, slug: res.data.slug, summary: res.data.summary,
          content: res.data.content, category: res.data.category,
          status: res.data.status, featured_image_url: res.data.featured_image_url ?? '',
        });
        setSlugManual(true);
      }
      setLoading(false);
    });
  }, [id, isEdit]);

  function update(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: value };
        if (field === 'title' && !slugManual) next.slug = toSlug(value);
        return next;
      });
    };
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      title: form.title.trim(), slug: form.slug.trim(), summary: form.summary.trim(),
      content: form.content, category: form.category.trim() || 'Engineering',
      status: form.status, featured_image_url: form.featured_image_url.trim() || null,
    };

    const res = isEdit
      ? await apiFetch<Article>(`/api/v1/admin/articles/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await apiFetch<Article>('/api/v1/admin/articles', { method: 'POST', body: JSON.stringify(payload) });

    if (!res.success) { setError(res.error?.message ?? 'Save failed.'); setSaving(false); return; }

    if (form.status === 'published' && REVALIDATE_URL) {
      await fetch(`${REVALIDATE_URL}?secret=${encodeURIComponent(REVALIDATE_SECRET)}&path=/articles/${res.data.slug}`).catch(() => {});
    }

    navigate('/articles');
  }

  if (loading) return <div className="p-8 text-sm text-zinc-400">Loading…</div>;

  const inputCls = 'w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-indigo-500 focus:outline-none';

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="mb-8 text-xl font-bold text-white">{isEdit ? 'Edit Article' : 'New Article'}</h1>
      <form onSubmit={handleSave} className="space-y-5">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Title *</label>
          <input required value={form.title} onChange={update('title')} placeholder="Why I Chose Hono Over Express" className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Slug *</label>
          <input required value={form.slug} onChange={(e) => { setSlugManual(true); update('slug')(e); }} className={`${inputCls} font-mono`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Summary *</label>
          <input required value={form.summary} onChange={update('summary')} placeholder="One sentence for the listing card." className={inputCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Category</label>
          <select value={form.category} onChange={update('category')} className={inputCls}>
            <option>Engineering</option>
            <option>System Design</option>
            <option>Performance</option>
            <option>Career</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Status</label>
          <select value={form.status} onChange={update('status')} className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-zinc-400">Content (MDX) *</label>
          <textarea required value={form.content} onChange={update('content')} rows={20} placeholder={`## Introduction\n\nStart writing your article here...\n\n\`\`\`typescript\n// code example\n\`\`\``} className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 font-mono text-sm text-zinc-200 placeholder-zinc-700 focus:border-indigo-500 focus:outline-none" />
        </div>
        {error && <p className="text-sm text-red-400">&#9888; {error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Article'}
          </button>
          <button type="button" onClick={() => navigate('/articles')} className="rounded-md border border-zinc-700 px-5 py-2 text-sm text-zinc-400 hover:text-white">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
